import styled from "styled-components";
import {
  Modal,
  ModalContent,
  ModalFooter,
  ModalTitle,
  Button,
  Text,
} from "suomifi-ui-components";
import { IconCheckCircle, IconWarning, IconInfo } from "suomifi-icons";
import { useTranslation, Trans } from "react-i18next";
import { useModaaliBackdropSulku } from "../utils/modaaliBackdropSulku";

/**
 * Henkilöllisyyden todentamisen valintamodaali (työpaja 22.6.2026, B1).
 *
 * Avautuu käyttäjäkortin "Todenna henkilöllisyys" -painikkeesta. Tarjoaa
 * kaksi reittiä secondary-painikkeina:
 *   1. Manuaaliprosessi (ylempänä) — normaali käsin todentaminen.
 *   2. ID-proofingin ohitus ("taikanappi", alempana) — poikkeustoiminto.
 *
 * B1: taikanappi ei ole enää oma punainen/ensisijainen painike käyttäjäkortilla
 * vaan "kaivetaan esiin" tämän valinnan kautta → ei heti tyrkyllä, ettei uusi
 * työntekijä paina sitä ensimmäisenä. Ei punaista (ei näytä poistolta);
 * poikkeustoiminnon painoarvo tulee varoitustekstistä, ei väristä.
 */

interface Props {
  visible: boolean;
  kayttajaNimi: string;
  /** Näytä manuaaliprosessi-vaihtoehto (oikeus: kayttajat.todenna_manuaalisesti). */
  naytaManuaaliprosessi: boolean;
  /** Näytä ID-proofingin ohitus -vaihtoehto (oikeus + kelpoisuusehdot täyttyvät). */
  naytaTaikanappi: boolean;
  onValitseManuaali: () => void;
  onValitseTaikanappi: () => void;
  onCancel: () => void;
}

const Valinnat = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.m};
  margin-top: ${({ theme }) => theme.spacing.m};

  & .fi-button {
    width: 100%;
  }
`;

const Valinta = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.xxs};
`;

/* Painikkeen alainen ohjeteksti: yhtenäinen tyyli molemmille vaihtoehdoille —
   ikoni + teksti samalla rivillä, 10px väli (theme.spacing.xs). Vain ikoni
   erottaa ne: neutraali i-ikoni (manuaaliprosessi) vs. oranssi varoituskolmio
   (ID-proofingin ohitus). */
const Ohje = styled.p`
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: ${({ theme }) => theme.spacing.xs};
  font: ${({ theme }) => theme.typography.bodyTextSmall};
  font-size: 0.8125rem;
  color: ${({ theme }) => theme.colors.depthDark1};

  & > svg {
    flex-shrink: 0;
    width: 1.125rem;
    height: 1.125rem;
    margin-top: 0.05rem;
  }
`;

const OhjeInfo = styled(Ohje)`
  & > svg .fi-icon-base-fill {
    fill: ${({ theme }) => theme.colors.depthDark1};
  }
`;

const OhjeVaroitus = styled(Ohje)`
  & > svg .fi-icon-base-fill {
    fill: ${({ theme }) => theme.colors.accentBase};
  }
`;

export default function TodennusValintaModal({
  visible,
  kayttajaNimi,
  naytaManuaaliprosessi,
  naytaTaikanappi,
  onValitseManuaali,
  onValitseTaikanappi,
  onCancel,
}: Props) {
  const { t } = useTranslation("todennusValinta");
  useModaaliBackdropSulku(visible, onCancel);

  return (
    <>
      <Modal
        appElementId="root"
        visible={visible}
        onEscKeyDown={onCancel}
        scrollable={false}
      >
        <ModalContent>
          <ModalTitle>{t("otsikko")}</ModalTitle>
          <Text>{t("kuvaus", { nimi: kayttajaNimi })}</Text>

          <Valinnat>
            {naytaManuaaliprosessi && (
              <Valinta>
                <Button
                  variant="secondary"
                  icon={<IconCheckCircle />}
                  onClick={onValitseManuaali}
                >
                  {t("manuaali.nappi")}
                </Button>
                <OhjeInfo>
                  <IconInfo aria-hidden />
                  <span>{t("manuaali.ohje")}</span>
                </OhjeInfo>
              </Valinta>
            )}

            {naytaTaikanappi && (
              <Valinta>
                <Button
                  variant="secondary"
                  icon={<IconWarning />}
                  onClick={onValitseTaikanappi}
                >
                  {t("taikanappi.nappi")}
                </Button>
                <OhjeVaroitus>
                  <IconWarning aria-hidden />
                  <span>
                    <Trans
                      ns="todennusValinta"
                      i18nKey="taikanappi.ohje"
                      components={{ b: <strong /> }}
                    />
                  </span>
                </OhjeVaroitus>
              </Valinta>
            )}
          </Valinnat>
        </ModalContent>
        <ModalFooter>
          <Button variant="secondaryNoBorder" onClick={onCancel}>
            {t("peruuta")}
          </Button>
        </ModalFooter>
      </Modal>
    </>
  );
}
